import React, {useState, useRef} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Undo,
  Redo,
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;
  disabled?: boolean;
  className?: string;
  showToolbar?: boolean;
  toolbarPosition?: "top" | "bottom";
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
  minHeight = "120px",
  maxHeight = "400px",
  disabled = false,
  className = "",
  showToolbar = true,
  toolbarPosition = "top",
}) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const linkInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "rich-text-link",
        },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({editor}) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "tiptap-content",
        style: `min-height: ${minHeight}; max-height: ${maxHeight};`,
      },
    },
  });

  if (!editor) {
    return null;
  }

  const addLink = () => {
    if (linkUrl) {
      // Ensure URL has proper protocol
      let formattedUrl = linkUrl.trim();
      if (
        !formattedUrl.startsWith("http://") &&
        !formattedUrl.startsWith("https://")
      ) {
        formattedUrl = "https://" + formattedUrl;
      }

      // Don't focus the editor immediately, let the input keep focus
      editor.chain().setLink({href: formattedUrl}).run();
      setLinkUrl("");
      setShowLinkInput(false);
      // Focus back to editor after a short delay
      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    }
  };

  // Helper function to execute editor commands with proper focus
  const executeCommand = (command: () => void) => {
    // Ensure editor is focused before executing command
    if (editor && !editor.isDestroyed) {
      editor.commands.focus();
      command();
    }
  };

  const ToolbarButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
  }> = ({onClick, isActive = false, disabled = false, title, children}) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      disabled={disabled}
      className={`tiptap-toolbar-btn ${isActive ? "is-active" : ""}`}
      title={title}
    >
      {children}
    </button>
  );

  const Toolbar = () => (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold"
        >
          <Bold size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic"
        >
          <Italic size={16} />
        </ToolbarButton>
      </div>

      <div className="tiptap-toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({level: 1}).run()}
          isActive={editor.isActive("heading", {level: 1})}
          title="Heading 1"
        >
          <Heading1 size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({level: 2}).run()}
          isActive={editor.isActive("heading", {level: 2})}
          title="Heading 2"
        >
          <Heading2 size={16} />
        </ToolbarButton>
      </div>

      <div className="tiptap-toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <ListOrdered size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => {
            executeCommand(() => {
              // For blockquote, use a more robust approach
              if (editor.isActive("blockquote")) {
                editor.chain().focus().toggleBlockquote().run();
              } else {
                // Wrap the current selection in a blockquote
                editor.chain().focus().setBlockquote().run();
              }
            });
          }}
          isActive={editor.isActive("blockquote")}
          title="Quote"
        >
          <Quote size={16} />
        </ToolbarButton>
      </div>

      <div className="tiptap-toolbar-group">
        <ToolbarButton
          onClick={() => {
            setShowLinkInput(!showLinkInput);
            // Focus the input when it appears
            if (!showLinkInput) {
              setTimeout(() => {
                linkInputRef.current?.focus();
              }, 10);
            }
          }}
          isActive={editor.isActive("link")}
          title="Add Link"
        >
          <LinkIcon size={16} />
        </ToolbarButton>
      </div>

      <div className="tiptap-toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo size={16} />
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo size={16} />
        </ToolbarButton>
      </div>

      {showLinkInput && (
        <div className="tiptap-link-input">
          <input
            ref={linkInputRef}
            type="url"
            placeholder="Enter URL..."
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                addLink();
              }
              if (e.key === "Escape") {
                setShowLinkInput(false);
                setLinkUrl("");
                editor.commands.focus();
              }
            }}
            onFocus={(e) => {
              // Prevent editor from stealing focus
              e.stopPropagation();
            }}
            className="env-input"
            autoFocus
          />
          <button
            type="button"
            onClick={addLink}
            className="btn btn-primary env-btn-small"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl("");
            }}
            className="btn btn-secondary env-btn-small"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`tiptap-editor ${className}`}>
      {showToolbar && toolbarPosition === "top" && <Toolbar />}

      <div className="tiptap-content-wrapper">
        <EditorContent
          editor={editor}
          className="tiptap-content"
          style={{minHeight, maxHeight}}
        />

        {!value && <div className="tiptap-placeholder">{placeholder}</div>}
      </div>

      {showToolbar && toolbarPosition === "bottom" && <Toolbar />}
    </div>
  );
};

export default RichTextEditor;
